/**
 * @bos/portal3 — what every ROOM of a run gets from the shell.
 *
 * The shell owns the one thing all rooms share: the run row and its journal (the SSE
 * stream, the status, the stall). Everything else a room needs it fetches itself, from
 * its own read surface, keyed on the run's status — which is why a deep link into a room
 * survives a refresh with no shell state to rebuild.
 *
 * Passed down react-router's `<Outlet context>` rather than a React context provider: the
 * rooms are route children, so the router already owns the wiring, and a room rendered
 * outside a run cannot compile.
 */
import { useOutletContext } from 'react-router-dom';
import type {
  AnswerRunResult,
  CheckpointAnswer,
  Run,
  RunIntakeSurface,
  RunQuestionsResult,
} from '@bos/portal3-server';
import type { OwnerCall, RailStage, RunView } from './model';

export interface RunRoomContext {
  readonly runId: string;
  /** The run row — never null inside a room (the shell renders its own states first). */
  readonly run: Run;
  readonly view: RunView;
  readonly rail: readonly RailStage[];
  /** Is the run row + journal in hand? (The per-room fetches gate on this.) */
  readonly ready: boolean;
  /** The refresh key every room's read surface re-reads on: the run's status. */
  readonly statusKey: string;
  readonly terminal: boolean;
  /** What the machine is waiting for, or null when it is not waiting on the owner. */
  readonly ownerCall: OwnerCall | null;
  /** A stop/resume is in flight (rooms that offer the resume act share the busy state). */
  readonly actionBusy: boolean;
  readonly onResume: () => void;

  /**
   * THE QUESTION ROUND, read once by the shell and handed to every room.
   *
   * It is the one read surface a room cannot fetch for itself: the shell needs it too (the
   * waiting banner names the rooms holding questions, and the nav marks them), and a round is
   * one document for the whole run — seven rooms fetching it seven times would be seven copies
   * of the same truth with seven chances to disagree.
   */
  readonly questions: RunQuestionsResult | null;
  readonly qStatus: 'idle' | 'loading' | 'ready' | 'none' | 'error';
  readonly submitAnswers: (answers: CheckpointAnswer[]) => Promise<AnswerRunResult>;
  /** The run's grounded brief, when it has one — the subject strip's source. Pre-intake runs
   *  carry none and every strip renders nothing. */
  readonly intake: RunIntakeSurface | undefined;
}

export function useRunRoom(): RunRoomContext {
  return useOutletContext<RunRoomContext>();
}
